import { FC, useCallback, useState } from "react";

import styles from "./styles.module.scss";
import Text from "../../../Text";
import { useJournalSettings } from "../../../../services/journalSettings";

const SUB_TABS = [
    { key: "goal", label: "Objectif" },
    { key: "fill", label: "Remplir le journal" },
    { key: "challenge", label: "Challenge & validation" },
] as const;

type SubTabKey = (typeof SUB_TABS)[number]["key"];

function formatMoney(amount: number): string {
    return `${amount.toLocaleString("fr-FR")}\u00a0$`;
}

const HowToUse: FC = () => {
    const journal = useJournalSettings();
    const [activeSubTab, setActiveSubTab] = useState<SubTabKey>("goal");

    const handleSubTabChange = useCallback((key: SubTabKey) => {
        setActiveSubTab(key);
    }, []);

    const demoBaseLabel = formatMoney(journal.demoAccountBase);
    const challengeRiskLabel = formatMoney(journal.challengeMoneyManagement);

    return (
        <div className={styles.account_form}>
            <Text variant="title" className={styles.title}>
                Comment utiliser le journal de trading ?
            </Text>

            <div className={styles.subTabs} role="tablist">
                {SUB_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeSubTab === tab.key}
                        className={`${styles.subTab} ${
                            activeSubTab === tab.key ? styles.subTabActive : ""
                        }`}
                        onClick={() => handleSubTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeSubTab === "goal" && <GoalTab />}
            {activeSubTab === "fill" && (
                <FillTab demoBaseLabel={demoBaseLabel} />
            )}
            {activeSubTab === "challenge" && (
                <ChallengeTab challengeRiskLabel={challengeRiskLabel} />
            )}
        </div>
    );
};

const GoalTab: FC = () => (
    <div className={styles.body}>
        <p className={styles.lead}>
            Suivre ta performance quotidienne et vérifier le respect des règles
            de l&apos;école.
        </p>

        <Text variant="body" className={styles.sectionIntro}>
            Deux indicateurs clés
        </Text>

        <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
                <span className={styles.kpiBadge}>1</span>
                <div>
                    <h3 className={styles.kpiTitle}>Jour vert / rouge</h3>
                    <p className={styles.kpiText}>
                        Selon le respect de la méthode sur la séance.
                    </p>
                </div>
            </div>
            <div className={styles.kpiCard}>
                <span className={styles.kpiBadge}>2</span>
                <div>
                    <h3 className={styles.kpiTitle}>% DAY WIN</h3>
                    <p className={styles.kpiText}>
                        Pourcentage de journées gagnantes sur une période.
                    </p>
                </div>
            </div>
        </div>
    </div>
);

type FillTabProps = {
    demoBaseLabel: string;
};

const FillTab: FC<FillTabProps> = ({ demoBaseLabel }) => (
    <div className={styles.body}>
        <article className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Consignes de remplissage</h3>
            <ul className={styles.bulletList}>
                <li>Compléter le journal chaque jour.</li>
                <li>
                    Remplir correctement chaque session et{" "}
                    <strong>chaque trade</strong> de la session, pour obtenir
                    le maximum de précision sur tes données.
                </li>
            </ul>
        </article>

        <article className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Mode non financé / financé</h3>
            <ul className={styles.bulletList}>
                <li>
                    En mode <strong>non financé</strong>, reporte uniquement la
                    performance du compte principal ({demoBaseLabel}). La base
                    de calcul du % PnL s&apos;appuie sur ce capital de
                    référence.
                </li>
                <li>
                    En mode <strong>financé</strong>, la base de calcul change&nbsp;:
                    elle utilise le capital déclaré dans ton statut financé.
                </li>
                <li>
                    Si tu es financé et que ton statut est à jour, tu peux
                    renseigner les données réelles de ton compte (gains / pertes
                    et capital).
                </li>
            </ul>
            <div className={styles.callout}>
                <strong>Astuce :</strong> maintiens ton statut financé à jour
                sur la page Payouts pour que les statistiques restent fiables.
            </div>
        </article>

        <article className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Gestion multi-comptes</h3>
            <ul className={styles.bulletList}>
                <li>
                    Si tu trades plusieurs comptes (PRO ou challenges) sans être
                    en statut financé à jour, n&apos;enregistre que le résultat
                    du compte principal.
                </li>
            </ul>
            <div className={styles.callout}>
                <strong>Exemple :</strong> +200&nbsp;$ sur deux comptes et un
                résultat challenge à part → hors mode financé à jour, on note
                seulement <strong>+200&nbsp;$</strong> (compte principal).
            </div>
        </article>
    </div>
);

type ChallengeTabProps = {
    challengeRiskLabel: string;
};

const ChallengeTab: FC<ChallengeTabProps> = ({ challengeRiskLabel }) => (
    <div className={styles.body}>
        <article className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Mode Challenge</h3>
            <ul className={styles.bulletList}>
                <li>
                    Les sessions déjà enregistrées en Challenge restent dans le
                    journal. Le filtre Challenges permet de les isoler ou de
                    les exclure.
                </li>
                <li>
                    Le money management Challenge correspond au risque
                    d&apos;échec configuré&nbsp;:{" "}
                    <strong>−{challengeRiskLabel}</strong>.
                </li>
                <li>
                    Les sessions en mode challenge ne seront pas comptabilisées
                    dans vos statistiques.
                </li>
            </ul>
            <div className={styles.callout}>
                L&apos;option Challenge n&apos;est plus proposée à la création
                d&apos;une session. Les sessions existantes restent
                consultables et filtrables.
            </div>
        </article>

        <article className={styles.infoCard}>
            <h3 className={styles.infoTitle}>Validation de la semaine</h3>
            <ul className={styles.bulletList}>
                <li>
                    Valider les résultats uniquement en fin de semaine, après la
                    dernière session.
                </li>
                <li>
                    Une fois validé,{" "}
                    <strong>retour arrière impossible</strong>.
                </li>
            </ul>
            <div className={`${styles.callout} ${styles.calloutWarn}`}>
                Vérifie bien tes saisies avant de valider la semaine.
            </div>
        </article>
    </div>
);

export default HowToUse;
